/**
 * @fileoverview Event emitter utilities for creating consistent step events
 * @module screen-view-v2/utils/eventEmitter
 *
 * Implements:
 * - FR-062: Package emits custom events for user interactions
 * - FR-063: Events include full context
 * - FR-064: Helper functions for all 8 event types
 *
 * Provides factory functions to create StepEvent objects with proper structure.
 * Ensures consistent event payloads across all interactive components.
 */

import type { StepNode } from '@segmently/schema-models/v2';
import { EventType, type StepEvent } from '../types/events';

/**
 * Parameters for creating a StepEvent
 */
interface CreateStepEventParams {
  /** Action identifier */
  action: string;
  /** StepNode containing the event source */
  step: StepNode;
  /** Optional element ID within the step */
  element?: string;
  /** Type of event */
  eventType: EventType;
}

/**
 * Factory function to create a StepEvent with validated structure.
 * FR-063: Ensures all events have consistent payload structure.
 *
 * @param params - Event parameters
 * @returns Fully structured StepEvent
 *
 * @example
 * const event = createStepEvent({
 *   action: 'navigate',
 *   step: currentStep,
 *   element: 'button-1',
 *   eventType: EventType.ButtonClick
 * });
 */
export const createStepEvent = (params: CreateStepEventParams): StepEvent => {
  return {
    action: params.action,
    step: params.step,
    element: params.element,
    eventType: params.eventType,
  };
};

/**
 * Create a ButtonClick event.
 * FR-064: Helper for ButtonClick event type.
 *
 * @param action - Action identifier from button schema
 * @param step - StepNode containing the button
 * @param element - Optional button element ID
 * @returns StepEvent with ButtonClick type
 *
 * @example
 * const event = createButtonClickEvent('subscribe', step, 'footer-button');
 */
export const createButtonClickEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.ButtonClick,
  });
};

/**
 * Create a ListItemSelect event.
 * FR-064: Helper for ListItemSelect event type.
 *
 * @param action - Action identifier (usually item ID or value)
 * @param step - StepNode containing the list
 * @param element - Optional list item element ID
 * @returns StepEvent with ListItemSelect type
 */
export const createListItemSelectEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.ListItemSelect,
  });
};

/**
 * Create a ListItemDeselect event.
 * FR-064: Helper for ListItemDeselect event type.
 *
 * @param action - Action identifier (usually item ID or value)
 * @param step - StepNode containing the list
 * @param element - Optional list item element ID
 * @returns StepEvent with ListItemDeselect type
 */
export const createListItemDeselectEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.ListItemDeselect,
  });
};

/**
 * Create a FieldChange event.
 * FR-064: Helper for FieldChange event type.
 *
 * @param action - New field value or change description
 * @param step - StepNode containing the field
 * @param element - Optional field element ID
 * @returns StepEvent with FieldChange type
 */
export const createFieldChangeEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.FieldChange,
  });
};

/**
 * Create a SliderMove event.
 * FR-064: Helper for SliderMove event type.
 *
 * @param action - Current slide index or slide identifier
 * @param step - StepNode containing the slider
 * @param element - Optional slider element ID
 * @returns StepEvent with SliderMove type
 */
export const createSliderMoveEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.SliderMove,
  });
};

/**
 * Create a SliderCycleComplete event.
 * FR-064: Helper for SliderCycleComplete event type.
 *
 * Emitted when the slider completes a full cycle (transitions from
 * the last slide back to the first slide).
 *
 * @param action - Action identifier (typically 'slider-cycle-complete')
 * @param step - StepNode containing the slider
 * @param element - Optional slider element ID
 * @returns StepEvent with SliderCycleComplete type
 */
export const createSliderCycleCompleteEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.SliderCycleComplete,
  });
};

/**
 * Create a PickerChange event.
 * FR-064: Helper for PickerChange event type.
 *
 * @param action - Selected picker value
 * @param step - StepNode containing the picker
 * @param element - Optional picker element ID
 * @returns StepEvent with PickerChange type
 */
export const createPickerChangeEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.PickerChange,
  });
};

/**
 * Create a CalendarSelect event.
 * FR-064: Helper for CalendarSelect event type.
 *
 * @param action - Selected date in ISO format or as string
 * @param step - StepNode containing the calendar
 * @param element - Optional calendar element ID
 * @returns StepEvent with CalendarSelect type
 */
export const createCalendarSelectEvent = (
  action: string,
  step: StepNode,
  element?: string
): StepEvent => {
  return createStepEvent({
    action,
    step,
    element,
    eventType: EventType.CalendarSelect,
  });
};
